import { useTranslation } from 'react-i18next';
import { REPORT_PRESETS, type ReportPreset } from '@optiflow/types';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';

export interface ReportFilters {
  preset: ReportPreset;
  from: string;
  to: string;
  branchId: string;
  compare: 'previous' | 'none';
}

/**
 * Period, comparison and branch. The branch list is the user's own accessible branches, and "All
 * my branches" is independent of the app's active branch. Dates are sent as calendar dates; the
 * server decides what each one means in each branch's timezone.
 */
export function ReportFilterBar({
  value,
  onChange,
  hideCompare = false,
}: {
  value: ReportFilters;
  onChange: (next: ReportFilters) => void;
  /** For a report with no comparison period, such as current stock. */
  hideCompare?: boolean;
}) {
  const { t } = useTranslation();
  const { branches } = useBranch();
  const set = (patch: Partial<ReportFilters>) => onChange({ ...value, ...patch });

  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex min-w-40 flex-1 flex-col gap-1 sm:flex-none">
        <span className="text-muted text-xs font-medium">{t('reports.filters.period')}</span>
        <Select
          id="report-preset"
          value={value.preset}
          options={REPORT_PRESETS.map((preset) => ({
            value: preset,
            label: t(`reports.presets.${preset}`),
          }))}
          onChange={(event) => set({ preset: event.target.value as ReportPreset })}
        />
      </label>
      {value.preset === 'CUSTOM' ? (
        <>
          <div className="w-40">
            <Input
              id="report-from"
              label={t('reports.filters.from')}
              type="date"
              value={value.from}
              onChange={(event) => set({ from: event.target.value })}
            />
          </div>
          <div className="w-40">
            <Input
              id="report-to"
              label={t('reports.filters.to')}
              type="date"
              value={value.to}
              onChange={(event) => set({ to: event.target.value })}
            />
          </div>
        </>
      ) : null}
      <label className="flex min-w-40 flex-1 flex-col gap-1 sm:flex-none">
        <span className="text-muted text-xs font-medium">{t('reports.filters.branch')}</span>
        <Select
          id="report-branch"
          value={value.branchId}
          options={[
            { value: '', label: t('reports.filters.allBranches') },
            ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
          ]}
          onChange={(event) => set({ branchId: event.target.value })}
        />
      </label>
      {hideCompare ? null : (
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            id="report-compare"
            type="checkbox"
            checked={value.compare === 'previous'}
            onChange={(event) => set({ compare: event.target.checked ? 'previous' : 'none' })}
          />
          <span>{t('reports.filters.compare')}</span>
        </label>
      )}
    </div>
  );
}
